/// <reference types="@types/googlemaps" />
/*global google*/

// ─────────────────────────────────────────────────────────────────────────────
// Terra Draw replacement for the deprecated google.maps.drawing DrawingManager
// (unavailable in Maps JS >= May 2026).
//
// Keeps the EXACT same Redux contract as the old DrawingManager-based version —
// finished geometry is converted into the same shapes the reducers expect
// ({ filterGeo }, { data }, { position }, { data: { position, radius } }) — so
// ShapeManager / ViewObjectManager keep rendering the *persistent* shapes
// unchanged.
//
// Per-shape draw/edit:
//   - polygon / line: drawn AND edited inside Terra Draw (select-mode flags give
//     whole-shape drag, vertex drag, midpoint insert, delete); committed via
//     featureToGeo. Native overlays were tried here but intermittently refused to
//     body-drag (the map's drag system aborts mid-gesture), so we keep Terra Draw
//     — which owns the gesture and never conflicts with a native overlay.
//   - circle: drawn by Terra Draw, then handed off to a native editable <Circle>
//     (Terra Draw can't resize-while-circular or sync to a typed radius).
//   - pin: drawn by Terra Draw, then handed off to a native draggable <Marker>
//     with the custom pin icon (Terra Draw points render as plain circles).
//
// NOTE: getGeoFromOverlay + reconstructDataForObject are re-exported UNCHANGED
// because ViewObjectManager.js imports them for its committed edit save/cancel
// paths (native overlays). deleteVertexOnRightClick is also exported for
// ViewObjectManager's committed polygon/line vertex deletion.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import { Circle, Marker } from "@react-google-maps/api";
import {
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  appNotify
} from 'store/actions';
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawLineStringMode,
  TerraDrawPointMode,
  TerraDrawCircleMode,
  TerraDrawSelectMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { Constants } from 'utilities/constants';
import { viewObjects } from 'store/explore/views';
import { formatValue } from "utilities/format";

// ── Re-exported UNCHANGED ─── consumed by ViewObjectManager.js for the
// committed edit-cancel / edit-save paths (native, non-deprecated overlays).
export function reconstructDataForObject(objectType, overlayType, obj) {
  switch (overlayType) {
    case 'polygon':
    case 'polyline':
      if (objectType === Constants.ViewsObjectType.PROPERTYDATA) {
        return { filterGeo: [...obj.filterGeo] };
      } else {
        return { data: [...obj.data] };
      }
    case 'circle':
      return { data: { position: { ...obj.data.position }, radius: obj.data.radius } };
    case 'marker':
      return { position: { ...obj.position } };
    case 'Distance Measuring':
      return { data: {
        ...obj.data,
        coordinates: [...obj.data.coordinates],
        totalData: { ...obj.data.totalData }
      } };
    default: return null;
  }
}

export function getGeoFromOverlay(objectType, e) {
  if (e.type === 'polygon' || e.type === 'polyline') {
    if (e.overlay && e.overlay.latLngs && e.overlay.latLngs.length === 1) {
      const vertices = e.overlay.getPath();
      if (vertices && vertices.getLength() > 0) {
        var points = [];
        for (var i = 0; i < vertices.getLength(); i++) {
          var xy = vertices.getAt(i);
          points.push({ lat: xy.lat(), lng: xy.lng() });
        }
        if (e.type === 'polygon') {
          const firstPoint = points[0];
          const lastPoint = points[points.length - 1];
          if (firstPoint.lat !== lastPoint.lat || firstPoint.lng !== lastPoint.lng) {
            points.push(points[0]); // close out polygon with initial point
          }
        }
        if (objectType === Constants.ViewsObjectType.PROPERTYDATA) {
          const area = google.maps.geometry.spherical.computeArea(vertices); // sq meters
          return {
            filterGeo: points,
            isOversize: area > Constants.ViewsPropertyDataGeoFilterAreaLimit
          };
        }
        const field = objectType === Constants.ViewsObjectType.PROPERTYDATA ? 'filterGeo' : 'data';
        return { [field]: points };
      }
    }
  } else if (e.type === 'marker') {
    const position = e.overlay.getPosition();
    return { position: { lat: position.lat(), lng: position.lng() } };
  } else if (e.type === 'circle') {
    const position = e.overlay.getCenter();
    const bounds = e.overlay.getBounds();
    const point = { lat: position.lat(), lng: position.lng() };
    const radius = e.overlay.getRadius();
    return { data: { position: point, radius, bounds } };
  }
  return null;
}

// Right-click a vertex of an editable native Polygon/Polyline to delete it
// (google.maps supports vertex drag + midpoint insert natively, but not
// deletion). Used by ViewObjectManager's committed-shape editing. `e.vertex` is
// set only for an existing vertex; keep the shape valid (polygon ≥ 3, line ≥ 2).
export function deleteVertexOnRightClick(instance, e, minVertices) {
  if (!instance || e.vertex == null) return;
  const path = instance.getPath();
  if (!path || path.getLength() <= minVertices) return;
  path.removeAt(e.vertex);
}

const MAX_VERTICES = 500; // mirrors the old onOverlayComplete length guard

// Custom pin marker icon (teardrop + inner circle) — Terra Draw points only
// render as plain circles, so pins are handed off to a native <Marker>.
const iconPath = 'M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z';
const circlePath = ' M-6,-30a6,6 0 1,0 12,0a6,6 0 1,0 -12,0';

// app drawingMode (from store/explore/views viewObjects) -> Terra Draw mode name
const MODE_MAP = {
  polygon: 'polygon',
  polyline: 'linestring',
  marker: 'point',
  circle: 'circle'
};

// Select-mode edit flags so a finished polygon/line can be dragged whole, have
// vertices dragged/inserted (midpoints), and deleted — all inside Terra Draw.
const SHAPE_EDIT_FLAGS = {
  feature: {
    draggable: true,
    coordinates: { midpoints: true, draggable: true, deletable: true }
  }
};

// Orange "editing" look (matches ViewObjectManager's CustomShape) for the polygon
// draw mode and the select-mode editing of finished polygons/lines.
const ORANGE = '#ffa300';
const DRAW_POLYGON_STYLE = { fillColor: ORANGE, fillOpacity: 0.3, outlineColor: ORANGE, outlineWidth: 3 };
const SELECT_STYLES = {
  selectedPolygonColor: ORANGE,
  selectedPolygonFillOpacity: 0.3,
  selectedPolygonOutlineColor: ORANGE,
  selectedPolygonOutlineWidth: 3,
  selectedLineStringColor: ORANGE,
  selectedLineStringWidth: 5,
  // Edit handles: orange with a white outline for contrast.
  selectionPointColor: ORANGE,
  selectionPointOutlineColor: '#ffffff',
  midPointColor: ORANGE,
  midPointOutlineColor: '#ffffff'
};

// Build the Terra Draw mode set once. Everything is orange to match the rest of
// the draw/edit UI.
function createModes() {
  return [
    new TerraDrawSelectMode({
      // polygon/line are edited in select mode; circle/pin hand off to native.
      flags: { polygon: SHAPE_EDIT_FLAGS, linestring: SHAPE_EDIT_FLAGS },
      styles: SELECT_STYLES
    }),
    new TerraDrawPolygonMode({ editable: true, styles: DRAW_POLYGON_STYLE }),
    new TerraDrawLineStringMode({
      editable: true,
      styles: { lineStringColor: ORANGE, lineStringWidth: 5 }
    }),
    new TerraDrawPointMode({
      editable: true,
      styles: {
        pointColor: '#ffa300',
        pointWidth: 9,
        pointOutlineColor: '#ffffff',
        pointOutlineWidth: 3
      }
    }),
    new TerraDrawCircleMode({
      styles: { fillColor: '#ffa300', fillOpacity: 0.15, outlineColor: '#ffa300', outlineWidth: 3 }
    })
  ];
}

function bboxCenter(ring) {
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  ring.forEach(p => {
    minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng);
  });
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
}

// Derive a circle's center + radius (meters) from the polygon ring
// TerraDrawCircleMode emits, so we can seed the native <Circle> on hand-off.
function circleCenterRadius(ring) {
  const center = bboxCenter(ring);
  const radius = google.maps.geometry.spherical.computeDistanceBetween(
    new google.maps.LatLng(center.lat, center.lng),
    new google.maps.LatLng(ring[0].lat, ring[0].lng)
  );
  return { center, radius };
}

// Convert a finished Terra Draw polygon/line feature into the Redux-bound shape
// (used at commit for the Terra-Draw-edited polygon/line types).
function featureToGeo(objectType, appMode, feature) {
  const g = feature.geometry;
  if (appMode === 'polyline' && g.type === 'LineString') {
    return { data: g.coordinates.map(([lng, lat]) => ({ lat, lng })) };
  }
  if (appMode === 'polygon' && g.type === 'Polygon') {
    const ring = g.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
    const first = ring[0], last = ring[ring.length - 1];
    if (first && last && (first.lat !== last.lat || first.lng !== last.lng)) {
      ring.push(first); // close ring (parity with getGeoFromOverlay)
    }
    if (objectType === Constants.ViewsObjectType.PROPERTYDATA) {
      const path = ring.map(p => new google.maps.LatLng(p.lat, p.lng));
      const area = google.maps.geometry.spherical.computeArea(path); // sq meters
      return { filterGeo: ring, isOversize: area > Constants.ViewsPropertyDataGeoFilterAreaLimit };
    }
    return { data: ring };
  }
  return null;
}

// Work around a Terra Draw Google Maps adapter bug: it attaches its pointer
// listeners to a map-internal div found via `querySelector('div[style*="z-index: 3;"]')`,
// and removes them on teardown by querying that selector AGAIN. Google rebuilds
// those divs during a draw, so teardown can target a different element and leak
// the pointerdown capture listener. Caching the lookup makes register() and
// unregister() use the SAME element, so the listener is reliably removed. Cache
// is reset before each register so a fresh draw session re-queries the live DOM.
function patchAdapterElementCache(adapter) {
  if (!adapter || adapter.__elPatched || typeof adapter.getMapEventElement !== 'function') return;
  const orig = adapter.getMapEventElement.bind(adapter);
  adapter.__elCache = {};
  adapter.getMapEventElement = function (e) {
    const key = (e === 'keyup' || e === 'keydown') ? 'key' : 'pointer';
    if (!adapter.__elCache[key]) adapter.__elCache[key] = orig(e);
    return adapter.__elCache[key];
  };
  adapter.__elPatched = true;
}
function resetAdapterElementCache(adapter) {
  if (adapter && adapter.__elPatched) adapter.__elCache = {};
}

function MapDrawingManager({
  mapRef,
  mapReady,
  activeDrawingObject,
  manualRadius,
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  appNotify
}) {
  const drawRef = useRef(null);
  const adapterRef = useRef(null);
  const [ready, setReady] = useState(false);
  const startedRef = useRef(false);
  const finishedIdRef = useRef(null); // Terra Draw feature id for polygon/line editing
  const onFinishRef = useRef(null);
  const finishWrapperRef = useRef(null);
  if (!finishWrapperRef.current) {
    // Stable listener whose identity never changes (so re-registering after each
    // start() is a dedup no-op), but always calls the latest onFinish closure.
    finishWrapperRef.current = id => onFinishRef.current && onFinishRef.current(id);
  }

  // Circle hand-off: native <Circle> for the uncommitted window (Terra Draw can't
  // resize-while-circular or sync to a typed radius). `placedCircle` holds the
  // initial center/radius and is intentionally NOT updated on drag/type, so the
  // controlled props stay stable and the imperative setRadius()/drag win.
  const [placedCircle, setPlacedCircle] = useState(null);
  const circleInstanceRef = useRef(null);

  // Pin hand-off: native draggable <Marker> with the custom pin icon.
  const [placedPin, setPlacedPin] = useState(null);
  const markerInstanceRef = useRef(null);

  // Keep latest activeDrawingObject reachable inside Terra Draw event handlers.
  const activeRef = useRef(activeDrawingObject);
  activeRef.current = activeDrawingObject;

  const drawingObjectType = activeDrawingObject?.type;
  const appMode = drawingObjectType ? viewObjects[drawingObjectType]?.drawingMode : null;
  const terraMode = appMode ? MODE_MAP[appMode] : null; // null for none / Distance Measuring
  const isPlaced = !!activeDrawingObject?.isPlaced;

  // Terra Draw stays live while a tool is active for polygon/line (it draws AND
  // edits them in select mode). For circle/pin it runs only until the shape is
  // placed; then we hand off to a native overlay and stop Terra Draw so its
  // pointer capture can't block the native overlay's dragging.
  const isTerraEdited = terraMode === 'polygon' || terraMode === 'linestring';
  const shouldRunTerra = !!terraMode && (isTerraEdited || (!placedCircle && !placedPin));

  // ── 1. Instantiate Terra Draw once the map projection is ready ──────────────
  useEffect(() => {
    if (!mapReady || !mapRef?.current || drawRef.current) return;
    const map = mapRef.current;

    const init = () => {
      const adapter = new TerraDrawGoogleMapsAdapter({ map, lib: google.maps, coordinatePrecision: 9 });
      patchAdapterElementCache(adapter); // make register/unregister symmetric — see helper
      const draw = new TerraDraw({ adapter, modes: createModes() });
      draw.on('finish', finishWrapperRef.current);
      adapterRef.current = adapter;
      drawRef.current = draw;
      setReady(true);
    };

    if (map.getProjection()) {
      init();
    } else {
      const l = map.addListener('projection_changed', () => {
        if (map.getProjection()) { google.maps.event.removeListener(l); init(); }
      });
    }

    return () => {
      if (drawRef.current) {
        if (startedRef.current) drawRef.current.stop();
        drawRef.current = null;
        startedRef.current = false;
        setReady(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, mapRef]);

  // ── 2. Run Terra Draw for the draw gesture + polygon/line editing ───────────
  useEffect(() => {
    if (!ready || !drawRef.current) return;
    const draw = drawRef.current;
    if (shouldRunTerra) {
      if (!startedRef.current) {
        resetAdapterElementCache(adapterRef.current);
        draw.start();
        draw.on('finish', finishWrapperRef.current); // dedup-safe; restores if stop() cleared it
        startedRef.current = true;
      }
      // While actively drawing, set the draw mode. Once placed (polygon/line),
      // onFinish has switched to 'select' for editing — don't override it.
      if (!isPlaced) draw.setMode(terraMode);
    } else if (startedRef.current) {
      draw.clear();
      draw.stop();
      startedRef.current = false;
      finishedIdRef.current = null;
      const map = mapRef.current;
      // Remove any leftover Terra Draw feature from the shared map.data layer.
      if (map?.data) map.data.forEach(f => map.data.remove(f));
      // Terra Draw disables map dragging while drawing/dragging; restore it.
      if (map && map.get('draggable') === false) map.setOptions({ draggable: true });
    }
  }, [shouldRunTerra, terraMode, isPlaced, ready, mapRef]);

  // Tear down the native draw overlays when no tool is active (cancel / commit).
  useEffect(() => {
    if (!terraMode) {
      setPlacedCircle(null);
      setPlacedPin(null);
    }
  }, [terraMode]);

  // ── 3. finish handler: mirror old onOverlayComplete ─────────────────────────
  function onFinish(id) {
    const active = activeRef.current;
    const feature = drawRef.current.getSnapshot().find(f => f.id === id);
    if (!feature) return;

    const g = feature.geometry;
    const vertexCount =
      g.type === 'Polygon' ? g.coordinates[0].length :
      g.type === 'LineString' ? g.coordinates.length : 0;
    if (vertexCount > MAX_VERTICES) {
      alert('Number of points drawn is too long. Try again with a less detailed shape.');
      drawRef.current.removeFeatures([id]);
      return;
    }

    if (!active?.id) { // cleared mid-draw before the object existed
      drawRef.current.removeFeatures([id]);
      return;
    }

    const drawingMode = viewObjects[active.type]?.drawingMode;

    if (g.type === 'Polygon' && drawingMode === 'circle') {
      // Circle → native <Circle>. Publish the drawn radius for CircleDetail.
      const { center, radius } = circleCenterRadius(g.coordinates[0].map(([lng, lat]) => ({ lat, lng })));
      drawRef.current.removeFeatures([id]);
      setPlacedCircle({ center, radius });
      setRadius(radius);
    } else if (g.type === 'Point') {
      // Pin → native <Marker> (custom icon).
      const [lng, lat] = g.coordinates;
      drawRef.current.removeFeatures([id]);
      setPlacedPin({ position: { lat, lng } });
    } else {
      // Polygon / line: keep the Terra Draw feature and switch to select mode so
      // the user can drag/edit it (SHAPE_EDIT_FLAGS). Committed via featureToGeo.
      finishedIdRef.current = id;
      drawRef.current.setMode('select');
      drawRef.current.selectFeature(id);
    }

    setExploreActiveDrawingObject({ ...active, isPlaced: true });
  }
  onFinishRef.current = onFinish; // keep the stable finish wrapper calling the latest closure

  // ── 3b. Live circle resize: drive the native <Circle> from the typed radius. ─
  useEffect(() => {
    const inst = circleInstanceRef.current;
    if (!inst || !(manualRadius > 0)) return;
    if (Math.abs(inst.getRadius() - manualRadius) < 0.5) return; // already in sync
    inst.setRadius(manualRadius);
  }, [manualRadius]);

  // ── 4. Commit on confirm (activeDrawingObject.isCreating) ───────────────────
  useEffect(() => {
    const drawType =
      activeDrawingObject?.type !== 'DistanceMeasuring' &&
      activeDrawingObject?.type !== 'Label';

    if (!activeDrawingObject?.isCreating || !drawType) return;

    const { id, type } = activeDrawingObject;
    const commitMode = viewObjects[type]?.drawingMode;
    let geo = null;
    if (commitMode === 'circle') {
      const inst = circleInstanceRef.current;
      if (!inst) return;
      const c = inst.getCenter();
      geo = { data: { position: { lat: c.lat(), lng: c.lng() }, radius: inst.getRadius(), bounds: inst.getBounds() } };
    } else if (commitMode === 'marker') {
      const inst = markerInstanceRef.current;
      if (!inst) return;
      const p = inst.getPosition();
      geo = { position: { lat: p.lat(), lng: p.lng() } };
    } else {
      // Polygon / line: read the (possibly edited) Terra Draw feature.
      if (!drawRef.current || finishedIdRef.current == null) return;
      const feature = drawRef.current.getSnapshot().find(f => f.id === finishedIdRef.current);
      geo = feature ? featureToGeo(type, commitMode, feature) : null;
    }

    if (geo) {
      if (geo.filterGeo) {
        if (geo.isOversize) {
          const sqMiles = sm => formatValue(sm * 0.00000038610215855, { type: 'fixed', decimals: 0, useSeparator: true });
          appNotify(`Area exceeds limit of ${sqMiles(Constants.ViewsPropertyDataGeoFilterAreaLimit)} square miles. Please lower the shape size and try again.`, 'error');
          return;
        }
        setExploreGeoFilter(id, geo.filterGeo);
        if (geo.data?.radius != null) setRadius(geo.data.radius);
      } else {
        updateExploreObjectById(id, geo);
        if (geo.data?.radius != null) setRadius(geo.data.radius);
      }
    }
    setExploreActiveDrawingObject(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawingObject]);

  // Only circle and pin need a native React overlay; polygon/line are painted and
  // edited by Terra Draw directly.
  if (placedCircle) {
    return (
      <Circle
        center={placedCircle.center}
        radius={placedCircle.radius}
        editable
        draggable
        options={{ fillColor: '#ffa300', fillOpacity: 0.15, strokeColor: '#ffa300', strokeWeight: 3, zIndex: 1000 }}
        onLoad={inst => { circleInstanceRef.current = inst; }}
        onUnmount={() => { circleInstanceRef.current = null; }}
        onRadiusChanged={() => { if (circleInstanceRef.current) setRadius(circleInstanceRef.current.getRadius()); }}
      />
    );
  }
  if (placedPin) {
    return (
      <Marker
        position={placedPin.position}
        draggable
        icon={{
          path: iconPath + circlePath,
          fillColor: '#ffa300',
          fillOpacity: 1,
          anchor: new google.maps.Point(0, 0),
          strokeWeight: 3.0,
          strokeColor: '#fff',
          scale: 0.9
        }}
        onLoad={inst => { markerInstanceRef.current = inst; }}
        onUnmount={() => { markerInstanceRef.current = null; }}
      />
    );
  }
  return null;
}

function mapState({ exploreMap, exploreViews }) {
  // NOTE: deliberately does NOT spread exploreShapesData (the old version did) —
  // none of it is used here and it caused needless re-renders on shapes changes.
  return {
    mapReady: exploreMap.bounds !== null && exploreMap.bounds !== undefined,
    activeDrawingObject: exploreViews.activeDrawingObject,
    manualRadius: exploreViews.manualRadius
  };
}

const mapDispatch = {
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  appNotify
};

export default compose(connect(mapState, mapDispatch))(MapDrawingManager);
